/**
 * F21 (Wave 2D) — admin payment-config API client.
 *
 * Endpoints:
 *   GET    /admin/venues/:venueId/payment-config               — payments:read
 *   GET    /admin/venues/:venueId/payment-config/override-view — payment_config:write
 *     (same response as the canonical GET; exists because content_moderator
 *      holds the override write permission but not payments:read — BP-08)
 *   PUT    /admin/venues/:venueId/payment-config/override      — payment_config:write
 *   DELETE /admin/venues/:venueId/payment-config/override      — payment_config:write
 *
 * Per the F06 admin pattern, tests stub `global.fetch` rather than swapping
 * the module via a vitest alias. Production reads go through `adminFetch`
 * which carries the bearer token + auto-refreshes on 401.
 */
import type {
  ApplyAdminOverrideInput,
  ClearAdminOverrideInput,
  VenuePaymentConfig,
} from '@bravemove/types';
import { adminFetch } from '../auth/apiClient';

interface DataEnvelope<T> {
  readonly data: T;
}

/**
 * `GET /admin/venues/:venueId/payment-config` response shape.
 *
 * `auditHistory` is the latest ≤20 `admin_audit_log` rows scoped to
 * `target_type='venue' AND target_id=:venueId AND action LIKE 'payment_config.%'`.
 * The admin detail page renders this in an `AuditLogTimeline` below the
 * config snapshot.
 */
export interface AdminPaymentConfigAuditEntry {
  readonly id: string;
  readonly action: string;
  readonly actorAdminId: string | null;
  /**
   * The acting admin's email — the identity field the route actually returns
   * (`admin_audit_log.actor_email`). There is no `actorDisplayName` on the
   * wire (BP-06).
   */
  readonly actorEmail: string | null;
  readonly payload: Record<string, unknown>;
  readonly createdAt: string;
}

export interface AdminPaymentConfigResponse {
  readonly config: VenuePaymentConfig;
  readonly venueUpdatedAt: string;
  readonly auditHistory: readonly AdminPaymentConfigAuditEntry[];
}

/**
 * PUT/DELETE override responses carry the updated config + If-Match token but
 * NOT `auditHistory` — callers merge into existing detail state.
 */
export interface AdminPaymentConfigWriteResponse {
  readonly config: VenuePaymentConfig;
  readonly venueUpdatedAt: string;
}

export const adminPaymentConfigApi = {
  /** GET /admin/venues/:venueId/payment-config — read venue config + audit. */
  detail: async (venueId: string): Promise<AdminPaymentConfigResponse> => {
    const body = await adminFetch<DataEnvelope<AdminPaymentConfigResponse>>(
      `/admin/venues/${encodeURIComponent(venueId)}/payment-config`,
    );
    return body.data;
  },

  /**
   * GET /admin/venues/:venueId/payment-config/override-view — same response
   * as `detail`, but gated on `payment_config:write` so the override page's
   * initial load works for content_moderator (which lacks `payments:read`).
   */
  overrideView: async (venueId: string): Promise<AdminPaymentConfigResponse> => {
    const body = await adminFetch<DataEnvelope<AdminPaymentConfigResponse>>(
      `/admin/venues/${encodeURIComponent(venueId)}/payment-config/override-view`,
    );
    return body.data;
  },

  /**
   * PUT /admin/venues/:venueId/payment-config/override — apply whole-config
   * override. Requires `payment_config:write` (super_admin + content_moderator).
   * Returns the updated venue config (now with `adminOverride` populated).
   */
  applyOverride: async (
    venueId: string,
    input: ApplyAdminOverrideInput,
  ): Promise<AdminPaymentConfigWriteResponse> => {
    const body = await adminFetch<DataEnvelope<AdminPaymentConfigWriteResponse>>(
      `/admin/venues/${encodeURIComponent(venueId)}/payment-config/override`,
      {
        method: 'PUT',
        body: input as unknown as Record<string, unknown>,
      },
    );
    return body.data;
  },

  /**
   * DELETE /admin/venues/:venueId/payment-config/override — clear override.
   * Per Decision 6, the override snapshot is retained as the venue's current
   * row; only the `admin_override_active` flag flips off so the owner can
   * resume editing.
   */
  clearOverride: async (
    venueId: string,
    input: ClearAdminOverrideInput,
  ): Promise<AdminPaymentConfigWriteResponse> => {
    const body = await adminFetch<DataEnvelope<AdminPaymentConfigWriteResponse>>(
      `/admin/venues/${encodeURIComponent(venueId)}/payment-config/override`,
      {
        method: 'DELETE',
        body: input as unknown as Record<string, unknown>,
      },
    );
    return body.data;
  },
};
